import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldAlert, PackagePlus, FileDown, Check, FileWarning } from "lucide-react";

export default function CDLedger() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [meds, setMeds] = useState([]);
  const [entries, setEntries] = useState([]);
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ medication_id: "", medication_name: "", action: "received", quantity: 1, expected_quantity: "", expected_balance: "", signature_name: "", witness_signature_name: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const [openDeliveries, setOpenDeliveries] = useState([]);

  useEffect(() => {
    api.get("/clients").then((r) => {
      setClients(r.data || []);
      if ((r.data || []).length && !clientId) setClientId(r.data[0].id);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [clientId]);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [m, l] = await Promise.all([
        api.get(`/clients/${clientId}/sections/medication`),
        api.get(`/emar/cd-ledger?client_id=${clientId}`),
      ]);
      const cdMeds = (m.data || []).filter((x) => x.is_controlled_drug === true || x.is_controlled_drug === "yes");
      setMeds(cdMeds);
      setEntries(l.data.entries || []);
      setBalances(l.data.balances || {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = async (med) => {
    setForm({ medication_id: med.id, medication_name: med.medication_name || med.name || "Medication", action: "received", quantity: 1, expected_quantity: "", expected_balance: "", signature_name: "", witness_signature_name: "", notes: "", delivery_note_id: "", delivery_note_item_id: "" });
    setOpen(true);
    try {
      const r = await api.get(`/delivery-notes/open-items?client_id=${clientId}&medication_id=${med.id}`);
      setOpenDeliveries(r.data || []);
    } catch (e) { setOpenDeliveries([]); }
  };

  const chooseDeliveryItem = (val) => {
    if (val === "__none__") {
      setForm((f) => ({ ...f, delivery_note_id: "", delivery_note_item_id: "", expected_quantity: "" }));
      return;
    }
    const it = openDeliveries.find((x) => x.item_id === val);
    if (!it) return;
    setForm((f) => ({
      ...f,
      delivery_note_id: it.delivery_note_id,
      delivery_note_item_id: it.item_id,
      expected_quantity: String(it.expected_quantity),
      quantity: f.quantity || it.expected_quantity,
    }));
  };

  const submit = async () => {
    if (!form.signature_name.trim() || !form.witness_signature_name.trim()) {
      toast.error("Both signatures are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_id: clientId,
        quantity: Number(form.quantity),
        expected_quantity: form.expected_quantity === "" ? null : Number(form.expected_quantity),
        expected_balance: form.expected_balance === "" ? null : Number(form.expected_balance),
      };
      const r = await api.post("/emar/cd-ledger", payload);
      if (r.data?.discrepancy) {
        toast.warning(`Discrepancy detected: variance ${r.data.discrepancy_delta > 0 ? "+" : ""}${r.data.discrepancy_delta}. Flagged to dashboard.`);
      } else {
        toast.success("Ledger entry saved");
      }
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const resolve = async (entry) => {
    const notes = window.prompt(`Resolve discrepancy for ${entry.medication_name}? Add investigation notes (optional):`, "");
    if (notes === null) return;
    setResolvingId(entry.id);
    try {
      await api.post(`/emar/cd-ledger/${entry.id}/resolve`, { resolution_notes: notes });
      toast.success("Discrepancy resolved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResolvingId(null); }
  };

  const actionBadge = (action) => (
    <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${
      action === "received" ? "bg-emerald-50 border-emerald-300 text-emerald-800"
        : action === "administered" ? "bg-sky-50 border-sky-300 text-sky-800"
        : action === "disposed" ? "bg-red-50 border-red-300 text-red-800"
        : action === "stock_check" ? "bg-violet-50 border-violet-300 text-violet-800"
        : "bg-slate-100 border-slate-300 text-slate-700"
    }`}>{action === "stock_check" ? "stock check" : action}</span>
  );

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="hidden lg:flex h-14 border-b border-slate-200 bg-white px-6 items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><ShieldAlert className="w-3 h-3 text-red-600" /> Clinical</div>
          <h1 className="font-heading text-xl font-semibold" data-testid="cd-title">Controlled Drugs Ledger</h1>
        </div>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="h-9 w-64" data-testid="cd-client"><SelectValue placeholder="Service user" /></SelectTrigger>
          <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
        </Select>
        <Button
          size="sm" variant="outline" className="h-9"
          onClick={async () => {
            const t = toast.loading("Preparing CD audit PDF…");
            try {
              await downloadBlob(`/emar/cd-ledger/pdf?client_id=${clientId}`, `cd-ledger-${clientId}.pdf`);
              toast.success("CD audit downloaded", { id: t });
            } catch (e) { toast.error(formatApiError(e), { id: t }); }
          }}
          data-testid="btn-download-cd-pdf"
        >
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download PDF
        </Button>
      </div>

      {/* Mobile / tablet header — Batch 7C */}
      <div className="lg:hidden border-b border-slate-200 bg-white px-4 py-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><ShieldAlert className="w-3 h-3 text-red-600" /> Clinical</div>
            <h1 className="font-heading text-base font-semibold truncate" data-testid="cd-title-mobile">Controlled Drugs Ledger</h1>
          </div>
          <Button
            size="sm" variant="outline" className="h-11 shrink-0"
            onClick={async () => {
              const t = toast.loading("Preparing CD audit PDF…");
              try {
                await downloadBlob(`/emar/cd-ledger/pdf?client_id=${clientId}`, `cd-ledger-${clientId}.pdf`);
                toast.success("CD audit downloaded", { id: t });
              } catch (e) { toast.error(formatApiError(e), { id: t }); }
            }}
            data-testid="btn-download-cd-pdf-mobile"
          >
            <FileDown className="w-4 h-4" />
          </Button>
        </div>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="h-11 w-full" data-testid="cd-client-mobile"><SelectValue placeholder="Service user" /></SelectTrigger>
          <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="p-4 lg:p-6 space-y-5">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="cd-loading">Loading ledger…</div>
        ) : meds.length === 0 ? (
          <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="cd-empty">
            No controlled drugs recorded for this service user. Mark medications as CD in their profile to start tracking custody.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cd-balances">
              {meds.map((m) => {
                const key = `${clientId}::${m.id}`;
                const bal = balances[key] ?? 0;
                return (
                  <div key={m.id} className="bg-white border border-slate-200 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4" data-testid={`cd-med-${m.id}`}>
                    <div className="flex-1 min-w-0 w-full">
                      <div className="font-medium text-slate-900 break-words">{m.medication_name || m.name}</div>
                      <div className="text-xs text-slate-600">{m.dose || m.dosage || ""}</div>
                      <div className="text-[10px] uppercase tracking-widest text-red-700 mt-1">Controlled drug</div>
                    </div>
                    <div className="text-left sm:text-right">
                      <div className="font-heading text-2xl font-semibold tabular-nums" data-testid={`cd-balance-${m.id}`}>{bal}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">on hand</div>
                    </div>
                    <Button size="sm" onClick={() => openAdd(m)} data-testid={`btn-cd-add-${m.id}`} className="h-11 w-full sm:w-auto bg-slate-900 hover:bg-slate-800">
                      <PackagePlus className="w-3.5 h-3.5 mr-1.5" /> Stock entry
                    </Button>
                  </div>
                );
              })}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Ledger entries</div>
              {entries.length === 0 ? (
                <div className="bg-white border border-slate-200 p-4 text-sm text-slate-500" data-testid="cd-entries-empty">No ledger entries yet.</div>
              ) : (
                <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="cd-entries">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-600">
                        <th className="px-3 py-2">When</th>
                        <th className="px-3 py-2">Medication</th>
                        <th className="px-3 py-2">Action</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Balance</th>
                        <th className="px-3 py-2">Signed by</th>
                        <th className="px-3 py-2">Witness</th>
                        <th className="px-3 py-2">Flag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e) => (
                        <tr key={e.id} className={`border-t border-slate-100 ${e.discrepancy && !e.resolved ? "bg-red-50" : ""}`} data-testid={`cd-entry-${e.id}`}>
                          <td className="px-3 py-2 text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</td>
                          <td className="px-3 py-2">{e.medication_name}</td>
                          <td className="px-3 py-2 capitalize">{actionBadge(e.action)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{e.quantity > 0 ? `+${e.quantity}` : e.quantity}</td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums">{e.balance_after}</td>
                          <td className="px-3 py-2 text-xs">{e.signature_name}</td>
                          <td className="px-3 py-2 text-xs">{e.witness_signature_name || "—"}</td>
                          <td className="px-3 py-2 text-xs">
                            {e.discrepancy && (
                              <div className="flex items-center gap-2 flex-wrap">
                                {e.resolved ? (
                                  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-slate-100 border-slate-300 text-slate-700" data-testid={`cd-flag-resolved-${e.id}`}>Resolved</span>
                                ) : (
                                  <>
                                    <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-red-50 border-red-300 text-red-800" data-testid={`cd-flag-${e.id}`}>
                                      Δ {e.discrepancy_delta > 0 ? "+" : ""}{e.discrepancy_delta}
                                    </span>
                                    <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={() => resolve(e)} disabled={resolvingId === e.id} data-testid={`btn-cd-resolve-${e.id}`}>
                                      <Check className="w-3 h-3 mr-1" /> Resolve
                                    </Button>
                                  </>
                                )}
                                <Button
                                  size="sm" variant="outline" className="h-6 text-[11px]"
                                  onClick={async () => {
                                    const t = toast.loading("Preparing investigation PDF…");
                                    try {
                                      await downloadBlob(`/emar/cd-ledger/${e.id}/investigation-pdf`, `cd-investigation-${e.id.slice(0,8)}.pdf`);
                                      toast.success("Investigation report downloaded", { id: t });
                                    } catch (err) { toast.error(formatApiError(err), { id: t }); }
                                  }}
                                  data-testid={`btn-cd-investigation-${e.id}`}
                                >
                                  <FileWarning className="w-3 h-3 mr-1" /> PDF
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cd-dialog">
          <DialogHeader>
            <DialogTitle>Stock entry — {form.medication_name}</DialogTitle>
            <DialogDescription>Two-signature witnessing is required for every CD stock movement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Action</Label>
              <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
                <SelectTrigger data-testid="cd-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="received">Received (pharmacy delivery)</SelectItem>
                  <SelectItem value="disposed">Disposed</SelectItem>
                  <SelectItem value="adjustment">Adjustment (+/-)</SelectItem>
                  <SelectItem value="stock_check">Stock check (physical count)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>
                {form.action === "stock_check" ? "Physical count" : `Quantity ${form.action === "adjustment" ? "(use negative for reduction)" : ""}`}
              </Label>
              <Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="cd-quantity" />
            </div>
            {form.action === "received" && (
              <>
                {openDeliveries.length > 0 && (
                  <div>
                    <Label>Link to open delivery note (optional)</Label>
                    <Select value={form.delivery_note_item_id || "__none__"} onValueChange={chooseDeliveryItem}>
                      <SelectTrigger data-testid="cd-delivery-note-select"><SelectValue placeholder="Pick a delivery note…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {openDeliveries.map((it) => (
                          <SelectItem key={it.item_id} value={it.item_id}>
                            {it.pharmacy_name} · {it.delivery_date} · {it.expected_quantity} expected
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Expected from delivery note (optional)</Label>
                  <Input type="number" step="0.01" value={form.expected_quantity} onChange={(e) => setForm({ ...form, expected_quantity: e.target.value })} placeholder="e.g. 28" data-testid="cd-expected-quantity" />
                  <div className="text-[11px] text-slate-500 mt-1">If actual received differs from the pharmacy delivery note, we'll flag the entry as a discrepancy on the dashboard.</div>
                </div>
              </>
            )}
            {form.action === "stock_check" && (
              <div>
                <Label>Expected balance (from records, optional)</Label>
                <Input type="number" step="0.01" value={form.expected_balance} onChange={(e) => setForm({ ...form, expected_balance: e.target.value })} placeholder="e.g. 24" data-testid="cd-expected-balance" />
                <div className="text-[11px] text-slate-500 mt-1">The physical count will be compared against the system balance. Any variance is flagged and the ledger is corrected to reality.</div>
              </div>
            )}
            <div>
              <Label>Signature (carer)</Label>
              <Input value={form.signature_name} onChange={(e) => setForm({ ...form, signature_name: e.target.value })} placeholder="Your full name" data-testid="cd-signature" />
            </div>
            <div>
              <Label>Witness signature (second person)</Label>
              <Input value={form.witness_signature_name} onChange={(e) => setForm({ ...form, witness_signature_name: e.target.value })} placeholder="Witness full name" data-testid="cd-witness" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cd-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="btn-cd-save" className="bg-slate-900 hover:bg-slate-800">{saving ? "Saving…" : "Sign & save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
